<script lang="ts" setup>
import { AdminUserContext } from '@/types';
import {
    PublicEntityKind,
    PublicEntityList,
    PublicEntitySearchField,
    PublicEntitySortField,
    QueryPublicEntityListArgs,
    SortOrder
} from '@/graphql/types-service';
import { useApolloClient } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { AutoCompleteCompleteEvent } from 'openvue/autocomplete';

const props = defineProps<{
    modelValue?: AdminUserContext | null;
}>();

const emit = defineEmits(['update:modelValue', 'select', 'close']);
const loading = ref(false);
const searchText = ref('');
const to = ref();
const searchFields = [
    PublicEntitySearchField.Id,
    PublicEntitySearchField.Name,
    PublicEntitySearchField.Address
];
const fields = ['name', 'id', 'address'];
const items = ref<any[] | undefined>([]);
const { client } = useApolloClient('service');
const innerValue = ref<AdminUserContext | null>(props.modelValue ?? null);
function onChange() {
    if (innerValue.value && typeof innerValue.value === 'object') {
        const v = innerValue.value as any;
        emit('update:modelValue', <AdminUserContext>{
            id: v.r,
            ty: v.ty,
            name: v.name,
            kind: v.kind
        });
        emit('select');
    }
}

const getVariables = (searchText?: string): QueryPublicEntityListArgs => {
    const text = searchText ?? '';
    return {
        input: {
            filter: {
                kinds: [
                    PublicEntityKind.Be,
                    PublicEntityKind.Ga,
                    PublicEntityKind.La,
                    PublicEntityKind.Mb,
                    PublicEntityKind.So,
                    PublicEntityKind.Wa
                ]
            },
            search: {
                text,
                fields: searchFields
            },
            sort: {
                by: PublicEntitySortField.Name,
                order: SortOrder.Asc
            }
        }
    };
};

async function onComplete(ev: AutoCompleteCompleteEvent) {
    let timeoutMs = 0;
    loading.value = true;
    if (to.value && searchText.value !== ev.query) {
        clearTimeout(to.value);
        to.value = null;
        timeoutMs = 600;
    }
    searchText.value = ev.query;
    to.value = setTimeout(async () => {
        items.value = undefined;
        const { data } = await client.query<
            { list: PublicEntityList },
            QueryPublicEntityListArgs
        >({
            query: gql`
                query PublicEntityList($input: PublicEntityListInput) {
                    list: publicEntityList(input: $input) {
                        items {
                            r
                            id
                            name
                            address
                            ty
                            kind
                        }
                    }
                }
            `,
            variables: getVariables(ev.query),
            context: {
                ignoreAdminCtx: true
            }
        });
        items.value = [
            ...(data?.list?.items ?? []).map((it) => ({
                r: it.r ?? '',
                id: it.id,
                name: it.name ?? '',
                address: it.address ?? '',
                ty: it.ty,
                kind: it.kind
            }))
        ];
        loading.value = false;
    }, timeoutMs);
}
</script>
<template>
    <div class="flex flex-col gap-2 mb-8">
        <div class="flex flex-row">
            <AutoComplete
                id="context"
                v-model="innerValue"
                option-label="name"
                dropdown
                data-key="r"
                input-class="w-full"
                data-testid="selectSubContext"
                auto-option-focus
                :suggestions="items"
                :loading="loading"
                overlay-class="context-select"
                class="w-full"
                selection-message=""
                @complete="onComplete"
                @change="onChange"
            >
                <template #option="slotProps">
                    <div class="flex-1 flex flex-col">
                        <div
                            v-for="(field, index) in fields"
                            :key="field"
                            :class="
                                index !== 0
                                    ? 'text-sm text-surface-500 dark:text-surface-400'
                                    : 'font-medium text-sm'
                            "
                        >
                            <HighlightedText
                                :search="searchText ?? ''"
                                :text="slotProps.option[field]"
                            />
                        </div>
                    </div>
                    <div class="flex">
                        <Chip>
                            <span
                                class="text-xs bg-primary text-primary-contrast rounded-full w-6 h-6 font-bold flex items-center justify-center"
                                >{{ slotProps.option.kind }}</span
                            >
                            <span class="text-sm ml-1 font-medium">{{
                                $t(
                                    `dx.public_entity.${slotProps.option.ty}_${slotProps.option.kind}`.toLowerCase()
                                )
                            }}</span>
                        </Chip>
                    </div>
                </template>
                <template #empty> {{ $t('ux.labels.no_entries') }} </template>
            </AutoComplete>
        </div>
    </div>
</template>
<style lang="scss">
.context-select {
    .p-inputicon {
        margin-top: -0.75rem;
    }
}
</style>
